import { AlertTriangle, CheckCircle2, ShieldQuestion } from 'lucide-react';
import type { PdfSignatureInfo, PdfSignatureKind, SignatureVerification } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';

const KIND_CHIP: Record<PdfSignatureKind, string> = {
  qualified: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  trusted_profile: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400',
  other: 'border-border bg-muted/40 text-muted-foreground',
};

function kindLabel(kind: PdfSignatureKind): string {
  return t(`signatures.kind.${kind}`);
}

/**
 * Who to show as the signer. For podpis zaufany the CERTIFICATE belongs to the
 * ministry seal, and the human is named only in the signature dictionary
 * (`declaredSignerName`) — so that name wins wherever it exists.
 */
export function signerLabel(sig: PdfSignatureInfo): string | null {
  return sig.declaredSignerName ?? sig.signerName ?? sig.signerIdentifier ?? null;
}

/** The certificate subject, shown separately when it is a seal, not the person. */
function sealLabel(sig: PdfSignatureInfo): string | null {
  if (!sig.declaredSignerName || !sig.signerName) return null;
  return sig.signerName === sig.declaredSignerName ? null : sig.signerName;
}

/** One-line human summary of who signed, used as tooltip/title text. */
export function signatureTooltip(sig: PdfSignatureInfo): string {
  const who = signerLabel(sig) ?? t('signatures.unknownSigner');
  return t(`signatures.integrity.${sig.integrity}`) + ' · ' + kindLabel(sig.kind) + ' · ' + who;
}

/**
 * Compact per-signature badges for dense rows (kartoteka, contractor docs):
 * a kind-colored badge with the signer in the tooltip; invalid integrity gets
 * a strong red marker so a tampered scan can never read as merely "signed".
 */
export function SignatureBadges({ verification }: { verification?: SignatureVerification | null }) {
  if (!verification || verification.signatures.length === 0) return null;
  return (
    <>
      {verification.signatures.map((sig, i) => (
        <Badge
          key={i}
          variant="outline"
          title={signatureTooltip(sig)}
          className={cn('font-normal', KIND_CHIP[sig.kind])}
        >
          {sig.integrity === 'invalid' ? (
            <AlertTriangle className="size-3 text-red-600 dark:text-red-400" />
          ) : sig.integrity === 'unverifiable' ? (
            <ShieldQuestion className="size-3 text-amber-600 dark:text-amber-500" />
          ) : (
            <CheckCircle2 className="size-3 text-emerald-600 dark:text-emerald-400" />
          )}
          {kindLabel(sig.kind)}
        </Badge>
      ))}
    </>
  );
}

/** Full signature block for the publication detail sheet: kind badge, integrity
 *  indicator, signer + masked id, issuer, claimed time, partial-coverage hint. */
export function SignatureDetails({ verification }: { verification: SignatureVerification }) {
  if (verification.signatures.length === 0) {
    return <p className="text-muted-foreground text-xs">{t('signatures.noSignature')}</p>;
  }
  // A signature that stops short of EOF is normal once ANOTHER valid signature
  // covers the whole file — that is exactly what a second signer appending an
  // incremental revision looks like. Say that instead of the bare "partial".
  const restIsSigned = verification.signatures.some(
    (s) => s.coverage === 'whole_document' && s.integrity === 'valid',
  );
  return (
    <div className="flex flex-col gap-1.5">
      {verification.signatures.map((sig, i) => (
        <div key={i} className="flex flex-col gap-0.5 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className={cn('font-normal', KIND_CHIP[sig.kind])}>
              {kindLabel(sig.kind)}
            </Badge>
            {sig.integrity === 'valid' ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="size-3.5" />
                {t('signatures.integrity.valid')}
              </span>
            ) : sig.integrity === 'invalid' ? (
              <span className="inline-flex items-center gap-1 font-semibold text-red-700 dark:text-red-400">
                <AlertTriangle className="size-3.5" />
                {t('signatures.integrity.invalid')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-500">
                <ShieldQuestion className="size-3.5" />
                {t('signatures.integrity.unverifiable')}
              </span>
            )}
            {sig.coverage === 'partial' && (
              <span className="text-muted-foreground">
                · {t(restIsSigned ? 'signatures.restCoveredByLater' : 'signatures.partialCoverage')}
              </span>
            )}
          </div>
          <div className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {signerLabel(sig) && (
              <span className="text-foreground">
                {signerLabel(sig)}
                {sig.signerIdentifier && <span className="text-muted-foreground"> · {sig.signerIdentifier}</span>}
              </span>
            )}
            {sealLabel(sig) && <span>{t('signatures.seal', { seal: sealLabel(sig)! })}</span>}
            {sig.issuer && <span>{t('signatures.issuer', { issuer: sig.issuer })}</span>}
            {sig.signedAt && <span>{t('signatures.signedAt', { date: new Date(sig.signedAt).toLocaleString() })}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
